import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { 
  CalendarDays,
  Clock,
  Plus,
  Trash2,
  CheckCircle,
  Bell,
  Dumbbell
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { format, parseISO, isToday, isTomorrow, isPast, addDays } from 'date-fns';

const LOGO_URL = '/logo.webp';

export const SchedulePage = () => {
  const { api } = useAuth();
  const [scheduledWorkouts, setScheduledWorkouts] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // New schedule form
  const [newSchedule, setNewSchedule] = useState({
    workout_id: '',
    workout_title: '',
    scheduled_time: '09:00',
    reminder: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schedulesRes, workoutsRes] = await Promise.all([
        api().get('/schedule'),
        api().get('/workouts')
      ]);
      setScheduledWorkouts(schedulesRes.data);
      setWorkouts(workoutsRes.data);
    } catch (err) {
      console.error('SchedulePage: failed to fetch schedule + workouts', err);
      toast.error('Could not load your schedule');
    }
    setLoading(false);
  };

  const handleWorkoutSelect = (workoutId) => {
    const workout = workouts.find(w => w.id === workoutId);
    if (workout) {
      setNewSchedule({
        ...newSchedule,
        workout_id: workoutId,
        workout_title: workout.title
      });
    }
  };

  const scheduleWorkout = async () => {
    if (!newSchedule.workout_id) {
      toast.error('Please select a workout');
      return;
    }

    try {
      await api().post('/schedule', {
        ...newSchedule,
        scheduled_date: format(selectedDate, 'yyyy-MM-dd')
      });
      toast.success('Workout scheduled!');
      setDialogOpen(false);
      setNewSchedule({ workout_id: '', workout_title: '', scheduled_time: '09:00', reminder: true });
      fetchData();
    } catch (err) {
      toast.error('Failed to schedule workout');
    }
  };

  const completeScheduled = async (scheduleId) => {
    try {
      await api().put(`/schedule/${scheduleId}/complete`);
      toast.success('Workout completed!');
      fetchData();
    } catch (err) {
      toast.error('Failed to mark as complete');
    }
  };

  const deleteScheduled = async (scheduleId) => {
    try {
      await api().delete(`/schedule/${scheduleId}`);
      toast.success('Schedule removed');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete schedule');
    }
  };

  // Get schedules for selected date
  const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
  const daySchedules = scheduledWorkouts.filter(s => s.scheduled_date === selectedDateStr);

  // Get upcoming schedules (next 7 days)
  const upcomingSchedules = scheduledWorkouts
    .filter(s => !s.completed && !isPast(parseISO(s.scheduled_date + 'T23:59:59')))
    .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date))
    .slice(0, 5);

  // Dates with schedules for calendar highlighting
  const scheduledDates = [...new Set(scheduledWorkouts.map(s => s.scheduled_date))];

  const getDateLabel = (dateStr) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    return format(date, 'EEE, MMM d');
  };

  return (
    <div className="min-h-screen bg-black" data-testid="schedule-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <img src={LOGO_URL} alt="Ascend & Surpass" className="h-10 w-10" />
            </Link>
            <h1 className="font-heading text-2xl text-white uppercase tracking-wider hidden md:block">Schedule</h1>
          </div>
          <nav className="flex items-center gap-6">
            <Link to="/dashboard" className="text-white/70 hover:text-primary transition-colors font-medium">Dashboard</Link>
            <Link to="/workouts" className="text-white/70 hover:text-primary transition-colors font-medium">Workouts</Link>
            <Link to="/progress" className="text-white/70 hover:text-primary transition-colors font-medium">Progress</Link>
            <Link to="/schedule" className="text-primary font-medium">Schedule</Link>
            <Link to="/profile" className="text-white/70 hover:text-primary transition-colors font-medium">Profile</Link>
          </nav>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10 flex items-center justify-between"
        >
          <div>
            <h2 className="font-heading text-4xl md:text-5xl text-white uppercase tracking-wider mb-2">
              Workout <span className="text-primary">Schedule</span>
            </h2>
            <p className="text-muted-foreground text-lg">Plan your workouts and stay consistent</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="h-12 px-6 bg-primary text-primary-foreground font-heading tracking-wider uppercase" data-testid="schedule-btn">
                <Plus className="mr-2 h-5 w-5" />
                Schedule Workout
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-white uppercase tracking-wider">Schedule Workout</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label className="text-white text-sm uppercase tracking-wider">Select Workout</Label>
                  <Select value={newSchedule.workout_id} onValueChange={handleWorkoutSelect}>
                    <SelectTrigger className="mt-2 h-12 bg-black border-border text-white" data-testid="workout-select">
                      <SelectValue placeholder="Choose a workout" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border max-h-60">
                      {workouts.map(w => (
                        <SelectItem key={w.id} value={w.id}>
                          {w.title} ({w.duration_minutes} min)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-white text-sm uppercase tracking-wider">Date</Label>
                  <p className="text-primary font-heading text-lg mt-1">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</p>
                  <p className="text-muted-foreground text-sm">Select date from calendar below</p>
                </div>

                <div>
                  <Label className="text-white text-sm uppercase tracking-wider">Time</Label>
                  <Input
                    type="time"
                    value={newSchedule.scheduled_time}
                    onChange={(e) => setNewSchedule({ ...newSchedule, scheduled_time: e.target.value })}
                    className="mt-2 h-12 bg-black border-border text-white"
                    data-testid="time-input"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-white text-sm uppercase tracking-wider">Reminder</Label>
                    <p className="text-muted-foreground text-sm">Get notified before workout</p>
                  </div>
                  <Switch 
                    checked={newSchedule.reminder}
                    onCheckedChange={(checked) => setNewSchedule({ ...newSchedule, reminder: checked })}
                  />
                </div>

                <Button 
                  onClick={scheduleWorkout}
                  className="w-full h-12 bg-primary text-primary-foreground font-heading tracking-wider uppercase"
                  data-testid="confirm-schedule-btn"
                >
                  <CalendarDays className="mr-2 h-5 w-5" />
                  Schedule
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Calendar */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1"
          >
            <div className="bg-card border border-border p-6">
              <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-4 flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-primary" />
                Select Date
              </h3>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                modifiers={{
                  scheduled: (date) => scheduledDates.includes(format(date, 'yyyy-MM-dd'))
                }}
                modifiersStyles={{
                  scheduled: { backgroundColor: 'rgba(255, 215, 0, 0.2)', color: '#FFD700', fontWeight: 'bold' }
                }}
                className="rounded-none border-border"
              />
            </div>

            {/* Upcoming */}
            <div className="bg-card border border-border p-6 mt-6">
              <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-4 flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                Upcoming
              </h3>
              {upcomingSchedules.length > 0 ? (
                <div className="space-y-3">
                  {upcomingSchedules.map((schedule) => (
                    <div key={schedule.id} className="bg-black p-3 border border-border">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium text-sm">{schedule.workout_title}</p>
                          <p className="text-primary text-xs font-heading">
                            {getDateLabel(schedule.scheduled_date)} at {schedule.scheduled_time}
                          </p>
                        </div>
                        {schedule.reminder && <Bell className="h-4 w-4 text-primary" />}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm text-center py-4">No upcoming workouts</p>
              )}
            </div>
          </motion.div>

          {/* Day View */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-2"
          >
            <div className="bg-card border border-border p-6">
              <h3 className="font-heading text-2xl text-white uppercase tracking-wider mb-2">
                {format(selectedDate, 'EEEE')}
              </h3>
              <p className="text-primary font-heading text-lg mb-6">{format(selectedDate, 'MMMM d, yyyy')}</p>

              {daySchedules.length > 0 ? (
                <div className="space-y-4">
                  {daySchedules.map((schedule) => {
                    const workout = workouts.find(w => w.id === schedule.workout_id);
                    return (
                      <div 
                        key={schedule.id} 
                        className={`bg-black border p-6 ${schedule.completed ? 'border-green-500/50' : 'border-border'}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-4">
                            <div className={`h-12 w-12 flex items-center justify-center ${schedule.completed ? 'bg-green-500/20' : 'bg-primary/20'}`}>
                              {schedule.completed ? (
                                <CheckCircle className="h-6 w-6 text-green-400" />
                              ) : (
                                <Dumbbell className="h-6 w-6 text-primary" />
                              )}
                            </div>
                            <div>
                              <h4 className="font-heading text-xl text-white uppercase tracking-wider">{schedule.workout_title}</h4>
                              <p className="text-primary font-heading text-lg">{schedule.scheduled_time}</p>
                              {workout && (
                                <p className="text-muted-foreground text-sm mt-1">
                                  {workout.duration_minutes} min · {workout.calories_burned} cal · {workout.category}
                                </p>
                              )}
                              {schedule.reminder && (
                                <div className="flex items-center gap-1 mt-2 text-muted-foreground text-sm">
                                  <Bell className="h-3 w-3" />
                                  <span>Reminder enabled</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {!schedule.completed && (
                              <>
                                <Button
                                  onClick={() => completeScheduled(schedule.id)}
                                  size="sm"
                                  className="bg-green-500 text-white hover:bg-green-600"
                                  data-testid={`complete-${schedule.id}`}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Complete
                                </Button>
                                <Link to={`/workout/${schedule.workout_id}`}>
                                  <Button size="sm" variant="outline" className="border-primary text-primary">
                                    Start
                                  </Button>
                                </Link>
                              </>
                            )}
                            <button 
                              onClick={() => deleteScheduled(schedule.id)}
                              className="text-muted-foreground hover:text-red-400 transition-colors p-2"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-16">
                  <CalendarDays className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground text-lg mb-4">No workouts scheduled for this day</p>
                  <Button 
                    onClick={() => setDialogOpen(true)}
                    className="bg-primary text-primary-foreground font-heading tracking-wider uppercase"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Schedule Workout
                  </Button>
                </div>
              )}
            </div>

            {/* Quick Schedule */}
            <div className="bg-card border border-border p-6 mt-6">
              <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-4">Quick Schedule</h3>
              <div className="grid grid-cols-7 gap-2">
                {Array.from({ length: 7 }).map((_, i) => {
                  const date = addDays(new Date(), i);
                  const dateStr = format(date, 'yyyy-MM-dd');
                  const hasSchedule = scheduledDates.includes(dateStr);
                  const isSelected = format(selectedDate, 'yyyy-MM-dd') === dateStr;
                  
                  return (
                    <button
                      key={dateStr}
                      onClick={() => setSelectedDate(date)}
                      className={`p-3 text-center transition-colors ${
                        isSelected 
                          ? 'bg-primary text-primary-foreground' 
                          : hasSchedule 
                            ? 'bg-primary/20 text-primary border border-primary/30' 
                            : 'bg-black border border-border text-white hover:border-primary/50'
                      }`}
                    >
                      <p className="text-xs uppercase">{format(date, 'EEE')}</p>
                      <p className="font-heading text-xl">{format(date, 'd')}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};
